import { z } from 'zod';
import { PersonRef } from './people.js';
/** File uploads (docs/adr/0014-file-uploads.md). */
export const FileKind = z.enum(['image', 'video', 'audio', 'pdf', 'other']);
export const FileInfo = z.object({
    id: z.uuid(),
    name: z.string(),
    contentType: z.string(),
    size: z.number().int(),
    kind: FileKind,
    status: z.enum(['pending', 'ready']),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    /** Short-lived signed links (1 hour). Null until the upload is complete. */
    url: z.string().nullable(),
    previewUrl: z.string().nullable(),
    uploadedBy: PersonRef.nullable(),
    createdAt: z.string(),
});
export const MAX_FILES_PER_MESSAGE = 10;
export const MAX_PREVIEW_BYTES = 2 * 1024 * 1024;
export const CreateUploadInput = z.object({
    name: z.string().trim().min(1).max(255),
    size: z.number().int().min(1),
    contentType: z.string().trim().min(1).max(255).regex(/^[\w.+-]+\/[\w.+-]+$/, 'Unknown file type'),
    /** Bytes of a browser-made preview (images only), uploaded to `previewUpload`. */
    previewSize: z.number().int().min(1).max(MAX_PREVIEW_BYTES).optional(),
});
export const UploadTarget = z.object({
    method: z.literal('PUT'),
    url: z.string(),
    /** Send exactly these headers with the PUT; they are part of the signature. */
    headers: z.record(z.string(), z.string()),
    expiresAt: z.string(),
});
export const CreatedUpload = z.object({ file: FileInfo, upload: UploadTarget, previewUpload: UploadTarget.nullable() });
export const CompleteUploadInput = z.object({
    width: z.number().int().min(1).max(100000).optional(),
    height: z.number().int().min(1).max(100000).optional(),
    hasPreview: z.boolean().optional(),
});
export const AttachFileInput = z.object({ fileId: z.uuid() });
const RASTER = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);
/** How a file is shown: raster images inline; everything else (SVG and HTML included) downloads. */
export function kindOf(contentType) {
    const t = contentType.toLowerCase();
    if (RASTER.has(t))
        return 'image';
    if (t.startsWith('video/'))
        return 'video';
    if (t.startsWith('audio/'))
        return 'audio';
    if (t === 'application/pdf')
        return 'pdf';
    return 'other';
}
export function displaysInline(contentType) {
    return RASTER.has(contentType.toLowerCase());
}
/** Executables and scripts are refused outright. */
const BLOCKED_EXTENSIONS = ['exe', 'msi', 'bat', 'cmd', 'com', 'scr', 'pif', 'cpl', 'vbs', 'vbe', 'js', 'jse', 'wsf', 'wsh', 'ps1', 'psm1', 'jar', 'app', 'dmg', 'pkg', 'apk', 'sh', 'reg', 'lnk', 'hta'];
export function blockedFileName(name) {
    const ext = name.toLowerCase().split('.').pop() ?? '';
    return name.includes('.') && BLOCKED_EXTENSIONS.includes(ext);
}
/** "Q3 report.pdf" → safe for a Content-Disposition header (ASCII fallback plus RFC 5987 UTF-8). */
export function contentDisposition(name, inline) {
    const ascii = name.replace(/[^\x20-\x7e]/g, '_').replace(/["\\]/g, '_');
    return `${inline ? 'inline' : 'attachment'}; filename="${ascii}"; filename*=UTF-8''${encodeURIComponent(name)}`;
}
export function formatBytes(n) {
    if (n < 1024)
        return `${n} B`;
    if (n < 1024 * 1024)
        return `${Math.round(n / 102.4) / 10} KB`;
    return `${Math.round(n / 104857.6) / 10} MB`;
}
//# sourceMappingURL=files.js.map